/*
 * assets/ce-member-notice.js — <ce-member-notice>
 *
 * Oeffnet das Hinweisfenster fuer Nicht-Mitglieder EINMAL JE SITZUNG.
 * Das Band darueber braucht dieses Skript nicht; es steht ohnehin im Markup.
 *
 * Warum <dialog> und kein nachgebautes Overlay: Fokusfalle, Esc, aria-modal,
 * inert fuer den Rest der Seite und die ::backdrop-Ebene macht der Browser.
 * Ein Nachbau muesste das alles koennen und koennte es schlechter
 * (02-theme-architecture.md §3.6).
 *
 * Einmal je Sitzung, nicht einmal je Besuch: sessionStorage, nicht
 * localStorage. Wer den Shop Tage spaeter wieder aufmacht, hat den Hinweis
 * vergessen — wer gerade durch zwoelf Produktseiten klickt, nicht.
 *
 * sessionStorage kann werfen (privater Modus, blockierte Speicherung). Jeder
 * Zugriff steht deshalb in try/catch, und im Fehlerfall gilt der Hinweis als
 * GESEHEN. Lieber kein Fenster als eines bei jedem Seitenaufruf.
 *
 * GEMERKT WIRD BEIM OEFFNEN, NICHT BEIM SCHLIESSEN.
 *
 * Zwei Gruende. Erstens inhaltlich: Wer die Seite verlaesst, ohne das Fenster
 * zu schliessen, hat es trotzdem gesehen — beim naechsten Klick noch einmal
 * damit anzufangen waere die schlechtere Erfahrung. "Einmal je Sitzung
 * gezeigt" ist die Zusage, nicht "einmal je Sitzung weggeklickt".
 *
 * Zweitens praktisch: Am 13.08.2026 fiel in der Vorschau auf, dass das
 * close-Event von <dialog> dort ueberhaupt nicht feuert — auch nicht bei
 * einem frisch erzeugten Dialog, also unabhaengig von diesem Code. Haenge die
 * Merkfunktion daran, kommt das Fenster bei jedem Seitenaufruf wieder. Ein
 * Zustand, den ein einziges nicht gefeuertes Event kaputtmacht, ist zu
 * zerbrechlich fuer etwas, das der Besucher auf JEDER Seite sieht.
 *
 * Im Theme-Editor geht es nicht von selbst auf: Der Merchant scrollt dort
 * durch Sektionen, ein Fenster nach jedem Neuladen waere unbedienbar. Zum
 * Ansehen reicht das Vorschau-Fenster des Editors.
 */

/* Gemessene Bandhoehe. Der Hero liest sie direkt, die Kopfzeile ueber die
   abgeleitete --ce-membernotice-sticky-h (unter 990px 0, gesetzt in
   section-header.css). */
const HOEHEN_VAR = '--ce-membernotice-h';

class CEMemberNotice extends window.CE.Element {
  setup(signal) {
    this._bandBeobachten(signal);

    this.dialog = this.querySelector('[data-notice-dialog]');

    // Kein Fenster in diesem Zustand, oder ein Browser ohne <dialog>: dann
    // traegt das Band die Aussage allein.
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

    // Schliessen muss immer gehen — auch wenn das Fenster gleich nicht von
    // selbst aufgeht. Der Theme-Editor oeffnet es zum Ansehen von Hand.
    this.querySelector('[data-notice-close]')?.addEventListener(
      'click',
      () => this.dialog.close(),
      { signal }
    );

    // Klick auf den Hintergrund schliesst. <dialog> macht das NICHT von
    // selbst; der Treffer liegt auf dem Dialog-Element, weil die Panel-Huelle
    // alle Klicks auf den Inhalt abfaengt.
    this.dialog.addEventListener(
      'click',
      (e) => {
        if (e.target === this.dialog) this.dialog.close();
      },
      { signal }
    );

    this.key = this.opt('sessionKey', 'ce-member-notice');

    if (window.CE.designMode) return;
    if (this._gesehen()) return;

    const delay = this.opt('delay', 0);
    if (delay > 0) {
      this._timer = setTimeout(() => this._oeffnen(), delay);
    } else {
      this._oeffnen();
    }
  }

  /* Das Band klebt oben, die Kopfzeile muss um seine Hoehe nach unten. Die
     Hoehe steht nicht fest: derselbe Satz braucht auf einem schmalen Geraet
     drei Zeilen und auf einem breiten eine.

     Gesetzt wird auf :root und nicht auf dem Element, weil section-header.css
     sie in einer ANDEREN Sektion liest. Ueber CSS-Vererbung kaeme sie dort
     nie an.

     DER DIREKTE AUFRUF TRAEGT, NICHT DER BEOBACHTER.

     Zuerst stand hier nur ein ResizeObserver. Am 13.08.2026 fiel in der
     Vorschau auf, dass dessen Rueckruf dort NIE feuert — auch nicht bei einem
     von Hand angelegten Observer auf demselben Element, also unabhaengig von
     diesem Code. Die Variable blieb leer, die Kopfzeile fiel auf 0 zurueck
     und waere beim Scrollen unter das Band gerutscht.

     Deshalb liegt die Last jetzt auf einer Messung, die garantiert laeuft:
     einem direkten Aufruf. resize, orientationchange und fonts.ready halten
     sie aktuell; der Observer ist Kuer und faengt nur noch den Fall ab, dass
     der Merchant den Text im Editor laenger macht. Faellt er aus, stimmt die
     Hoehe trotzdem. */
  /* AUFRUNDEN, NICHT RUNDEN. Beide Leser der Variable wollen lieber einen
     Pixel zu viel als einen zu wenig: die Kopfzeile klebt darunter und
     ueberlappte das Band sonst um den Bruchteil, und der Hero zieht sie von
     100svh ab (section-hero-showcase.css §1b) und ragte sonst genau so weit
     unter die Viewportkante. Bei 109,2px gemessener Bandhoehe waren das mit
     Math.round 0,2px Ueberstand — wenig, aber auf einem hochaufloesenden
     Schirm ein sichtbarer Streifen der Sektion darunter. */
  _bandMessen() {
    if (!this.isConnected) return;
    const bar = this.querySelector('.ce-membernotice__bar');
    if (!bar) return;
    document.documentElement.style.setProperty(
      HOEHEN_VAR,
      `${Math.ceil(bar.getBoundingClientRect().height)}px`
    );
  }

  _bandBeobachten(signal) {
    if (!this.querySelector('.ce-membernotice__bar')) return;

    this._bandMessen();

    /* Der Versatz zaehlt NUR, waehrend gescrollt wird — im Ruhezustand
       stehen Band und Kopfzeile ohnehin im Fluss untereinander. Deshalb
       haengt die Nachmessung am Scrollen: das ist genau dann garantiert da,
       wenn der Wert gebraucht wird, und es deckt jede Ursache ab, die die
       Hoehe veraendert haben koennte — Drehung, Zoom, spaet geladene Schrift,
       ein im Editor verlaengerter Text.

       Gedrosselt ueber einen Zeitstempel und NICHT ueber
       requestAnimationFrame: rAF ruht in Hintergrundtabs und in
       nicht-zeichnenden Ansichten, und eine Messung, die dann ausbleibt,
       waere genau die Zerbrechlichkeit, die hier vermieden werden soll. Fuenf
       Messungen je Sekunde sind ein getBoundingClientRect — vernachlaessigbar
       gegen das Neuzeichnen, das der Scroll ohnehin ausloest. */
    let zuletzt = 0;
    addEventListener(
      'scroll',
      () => {
        const jetzt = Date.now();
        if (jetzt - zuletzt < 200) return;
        zuletzt = jetzt;
        this._bandMessen();
      },
      { signal, passive: true }
    );

    /* Die drei naheliegenden Ausloeser bleiben zusaetzlich stehen — sie
       korrigieren frueher als der erste Scroll. Verlassen darf sich das
       Layout auf keinen von ihnen: in der Vorschau feuerte am 13.08.2026
       weder `resize` bei Viewport-Aenderung noch ein ResizeObserver. */
    addEventListener('resize', () => this._bandMessen(), { signal, passive: true });
    addEventListener('orientationchange', () => this._bandMessen(), { signal });
    document.fonts?.ready.then(() => this._bandMessen());

    if (typeof ResizeObserver === 'function') {
      this._ro = new ResizeObserver(() => this._bandMessen());
      this._ro.observe(this.querySelector('.ce-membernotice__bar'));
    }
  }

  /* clearTimeout ist Pflicht: ein AbortSignal raeumt Listener ab, aber keine
     Timer. Feuert der Timer, nachdem der Editor die Sektion ersetzt hat,
     ruft er showModal() auf totem DOM. Dasselbe gilt fuer den
     ResizeObserver — und die Variable muss weg, sonst behaelt die Kopfzeile
     einen Abstand zu einem Band, das es nicht mehr gibt. */
  teardown() {
    clearTimeout(this._timer);
    this._timer = null;
    this._ro?.disconnect();
    this._ro = null;
    document.documentElement.style.removeProperty(HOEHEN_VAR);
  }

  _oeffnen() {
    // Zwischen Timer-Start und Ablauf kann die Sektion verschwunden sein.
    if (!this.isConnected || this.dialog.open) return;
    this.dialog.showModal();
    // Sofort merken, nicht erst beim Schliessen — siehe Dateikopf.
    this._merken();
  }

  _gesehen() {
    try {
      return sessionStorage.getItem(this.key) === '1';
    } catch {
      return true;
    }
  }

  _merken() {
    try {
      sessionStorage.setItem(this.key, '1');
    } catch {
      /* Speicher nicht verfuegbar — dann bleibt es beim einen Aufruf. */
    }
  }
}

customElements.define('ce-member-notice', CEMemberNotice);
